import { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { Endpoint, isContrato, Report, ReportService, Route, StipulatorDialogService } from '@philips-emr/hpms-stipulator-commons';
import StipulatorContext from '@philips-emr/hpms-stipulator-commons/lib/contexts/stipulator-context';
import FilterService from '@philips-emr/hpms-stipulator-commons/lib/services/filter-service';
import { HpmsCustomCardEvent, HpmsCustomCardMenuRenderEvent } from '@philips-emr/hpms-stipulator-components';
import { HPMSOnCreateMenu, HPMSOnCreateOption, HPMSOnOptionClick } from '@philips-emr/hpms-stipulator-components/lib/events/event';
import { FetchAPI } from '@philips-emr/tws-components';
import Controller, { OnDynamicContentRender, OnMount, OnSubmit, OnUnmount } from '@philips-emr/tws-core/lib/client';
import { MountEvent, RenderEvent } from '@philips-emr/tws-core/lib/component/types';
import { HistoryService, ModalService } from '@philips-emr/tws-core/lib/services';

import { ReinclusaoBeneficiarioConsultaAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';
import { getLoteStampBySituacao, LoteSituacao } from '../lote/lote-utils';

enum SituacaoAlteracao {
  ABERTO = '1',
  AGUARDANDO = '2',
  APROVADO = '3',
  REJEITADO = '4',
}

enum SorN {
  S = 'S',
  N = 'N',
}

interface Data {
  dsStatus: string;
  ieStatus: SituacaoAlteracao;
  nrSeqReinclusao: number;
  nrSeqSolicRescBenef: number;
  ieConfirmaReinclusao: SorN;
}

const OptionKey = {
  REINCLUIR: 'reincluir',
  IMPRIMIR: 'imprimir',
  DETALHE: 'detalhe',
};

@Controller({ fragment: FragmentNames.REINCLUSAO_BENEFICIARIO_CONSULTA })
export default class ReinclusaoBeneficiarioConsultaController extends BaseController {

  @Inject('fetch-consulta')
  private fetchAPI: FetchAPI;

  private reportService: ReportService;

  private exibir: boolean;

  @OnMount(Aliases.FILTER_CONSULTA)
  onFilterMount(event: MountEvent): void {
    const params = {
      dtInicio: null,
      dtFim: null,
    };

    FilterService.restoreFilterState(event, params);
  }

  @OnUnmount(Aliases.FILTER_CONSULTA)
  onUnmountFilter(event: MountEvent): void {
    FilterService.persistFilterState(event);
  }

  @OnSubmit(Aliases.FILTER_CONSULTA)
  onSubmitFilter(): void {

    const { nrSeqSubestipulante } = StipulatorContext.getCurrentContract();
    if (nrSeqSubestipulante) {
      nrSeqSubestipulante && this.fetchAPI.setParameters({ ...this.fetchAPI.getParameters(), nrSeqSubestipulante });
    }

    void this.exibirImprimir().then((resp: boolean) => this.exibir = resp);

  }

  @OnDynamicContentRender(Aliases.RENDER_STATUS)
  public onDynamicContentRenderCardLote(event: RenderEvent): void {
    const { data, render } = event;
    const { dsStatus, ieStatus } = data as Data;

    const situacaoMap = {
      [SituacaoAlteracao.REJEITADO]: LoteSituacao.REJEITADA_PELA_OPERADORA,
      [SituacaoAlteracao.ABERTO]: LoteSituacao.AGUARDANDO_ACAO_DO_USUARIO,
      [SituacaoAlteracao.APROVADO]: LoteSituacao.INTEGRADO_A_PROPOSTA,
      [SituacaoAlteracao.AGUARDANDO]: LoteSituacao.LIBERADO,
    };

    const content = (): ReactElement => (getLoteStampBySituacao(situacaoMap[ieStatus], dsStatus));
    render(content);
  }

  @HPMSOnOptionClick(Aliases.HPMS_CUSTOM_CARD)
  public onOptionClick(event: HpmsCustomCardEvent<Data>): void {
    const { data, key } = event;
    const { nrSeqSolicRescBenef, nrSeqReinclusao, ieConfirmaReinclusao } = data;

    const action = {
      [OptionKey.REINCLUIR]: () => this.getReinclusao(nrSeqSolicRescBenef, ieConfirmaReinclusao),
      [OptionKey.DETALHE]: () => this.getMotivoRescisao(nrSeqSolicRescBenef, nrSeqReinclusao),
      [OptionKey.IMPRIMIR]: () => this.imprimir(nrSeqReinclusao),
    };

    if (action[key]) {
      action[key]();
    }

  }

  @HPMSOnCreateMenu(Aliases.HPMS_CUSTOM_CARD)
  public onCreateCardMenu(event: HpmsCustomCardMenuRenderEvent<never>): void {
    const { shouldRender } = event;
    shouldRender(isContrato());
  }

  @HPMSOnCreateOption(Aliases.HPMS_CUSTOM_CARD)
  public onCreateOption(event: HpmsCustomCardMenuRenderEvent<Data>): void {
    const { shouldRender, data, key } = event;
    const { ieStatus } = data;
    this.reportService = new ReportService('21', Endpoint.GET.RELATORIOS_POR_GRUPO);

    const show = {
      [OptionKey.DETALHE]: SituacaoAlteracao.REJEITADO === ieStatus,
      [OptionKey.REINCLUIR]: [SituacaoAlteracao.ABERTO, SituacaoAlteracao.AGUARDANDO].includes(ieStatus),
      [OptionKey.IMPRIMIR]: this.exibir,
    };

    shouldRender(show[key]);
  }

  private pushRescisaoRoute(nrSeqBeneficiario: number): void {
    const { nrSeqContrato } = StipulatorContext.getCurrentContract();

    HistoryService.push(Route.REINCLUIR_BENEFICIARIO_DETALHES, { nrSeqContrato, nrSeqBeneficiario });
  }

  private getReinclusao(nrSeqBeneficiario: number, ieConfirmaReinclusao: SorN): void {
    if (ieConfirmaReinclusao === SorN.S) {
      StipulatorDialogService.SN({
        description: 'stipulator.alerta-rescisao-30-deseja-proceguir-reinclusao',
        onSim: () => this.pushRescisaoRoute(nrSeqBeneficiario),
      });

      return;
    }

    this.pushRescisaoRoute(nrSeqBeneficiario);
  }

  private getMotivoRescisao(nrSeqBeneficiario: number, nrSeqReinclusao: number): void {
    ModalService.openDialog({
      title: 'stipulator.solicitacao-rejeitada-pela-operadora',
      fragment: 'reinclusao-rejeicao-feedback',
      contextData: {
        nrSeqBeneficiario,
        nrSeqReinclusao,
      },
    });
  }

  private exibirImprimir = async (): Promise<boolean> => {
    const service = new ReportService('21', Endpoint.GET.RELATORIOS_POR_GRUPO);
    const relatorios = await service.getRelatoriosByCdGrupo() as unknown as Report[];
    this.exibir = relatorios.length > 0;
    return relatorios.length > 0;
  };

  private imprimir(nrSeqReinclusao: number): void {
    this.reportService = new ReportService('21', Endpoint.GET.RELATORIOS_POR_GRUPO);
    this.reportService.abreListaRelatorios({ NR_SEQ_REINCLUSAO: nrSeqReinclusao });
  }

}
